import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import {
  ArrowLeft, Mail, Calendar, Users, FileText, Building2, ChevronLeft,
  ChevronRight, ChevronsLeft, ChevronsRight,
} from 'lucide-react';

interface FreeProfileCoach {
  id: string;
  userId: string;
  assessmentCode: string;
  niche: string | null;
  country: string | null;
  createdAt: string | null;
  onboardingCompleted: boolean | null;
  partnerName: string | null;
  user: {
    fullName: string;
    email: string;
  };
  stats: {
    totalRespondents: number;
    completedAssessments: number;
    lastActiveDate: string | null;
  };
}

export function FreeProfileCoaches() {
  const { user } = useAuth();
  const [coaches, setCoaches] = useState<FreeProfileCoach[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [partnerFilter, setPartnerFilter] = useState('all');
  const [partners, setPartners] = useState<{ id: string; company_name: string }[]>([]);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  useEffect(() => {
    if (!user) return;
    loadCoaches();
    loadPartners();
  }, [user]);

  const loadPartners = async () => {
    const { data } = await supabase.from('partners').select('id, company_name').order('company_name');
    if (data) setPartners(data);
  };

  const loadCoaches = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: coachesData, error: coachesError } = await supabase
        .from('coaches')
        .select('id, user_id, assessment_code, free_profile_coaching_niche, free_profile_country, created_at, free_profile_onboarding_completed, partner_id')
        .eq('account_plan', 'FREE_PROFILE_COACH')
        .order('created_at', { ascending: false });

      if (coachesError) throw coachesError;
      if (!coachesData) { setCoaches([]); return; }

      const results = await Promise.all(
        coachesData.map(async (coach) => {
          const [userRes, respondentsRes, completionsRes, lastResultRes, partnerRes] = await Promise.all([
            supabase.from('users').select('full_name, email').eq('id', coach.user_id).maybeSingle(),
            supabase.from('respondents').select('*', { count: 'exact', head: true }).eq('coach_id', coach.id),
            supabase.from('results').select('*', { count: 'exact', head: true }).eq('coach_id', coach.id),
            supabase.from('results').select('completed_at').eq('coach_id', coach.id).order('completed_at', { ascending: false }).limit(1).maybeSingle(),
            coach.partner_id
              ? supabase.from('partners').select('company_name').eq('id', coach.partner_id).maybeSingle()
              : Promise.resolve({ data: null, error: null }),
          ]);

          return {
            id: coach.id,
            userId: coach.user_id,
            assessmentCode: coach.assessment_code,
            niche: coach.free_profile_coaching_niche || null,
            country: coach.free_profile_country || null,
            createdAt: coach.created_at || null,
            onboardingCompleted: coach.free_profile_onboarding_completed || false,
            partnerName: partnerRes.data?.company_name || null,
            user: {
              fullName: userRes.data?.full_name || '',
              email: userRes.data?.email || '',
            },
            stats: {
              totalRespondents: respondentsRes.count || 0,
              completedAssessments: completionsRes.count || 0,
              lastActiveDate: lastResultRes.data?.completed_at || null,
            },
          } as FreeProfileCoach;
        })
      );

      setCoaches(results);
    } catch (err: any) {
      setError(err?.message || 'Failed to load free profile coaches');
    } finally {
      setLoading(false);
    }
  };

  const filteredData = useMemo(() => {
    return coaches.filter((coach) => {
      const matchesSearch =
        !search ||
        coach.user.fullName.toLowerCase().includes(search.toLowerCase()) ||
        coach.user.email.toLowerCase().includes(search.toLowerCase()) ||
        coach.assessmentCode.toLowerCase().includes(search.toLowerCase());

      const matchesPartner =
        partnerFilter === 'all' || coach.partnerName === partnerFilter;

      return matchesSearch && matchesPartner;
    });
  }, [coaches, search, partnerFilter]);

  const pageCount = Math.ceil(filteredData.length / pageSize);
  const pagedData = filteredData.slice(page * pageSize, (page + 1) * pageSize);

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <div className="text-stone-500">Loading free profile coaches…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900 mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to Admin Dashboard
        </Link>

        <div className="mb-8">
          <h1 className="text-3xl font-serif text-stone-900 mb-2">Free Profile Coaches</h1>
          <p className="text-stone-600">
            Coaches who signed up via the free profile link. All are attributed to a partner.
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
            {error}
          </div>
        )}

        <div className="mb-6 flex flex-wrap gap-3">
          <input
            type="text"
            placeholder="Search by name, email, or code…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            className="flex-1 min-w-[200px] px-4 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-40"
          />
          <select
            value={partnerFilter}
            onChange={(e) => { setPartnerFilter(e.target.value); setPage(0); }}
            className="px-4 py-2 border border-stone-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-40"
          >
            <option value="all">All Partners</option>
            {partners.map((p) => (
              <option key={p.id} value={p.company_name}>{p.company_name}</option>
            ))}
          </select>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-stone-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-stone-50 border-b border-stone-200">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Coach</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Partner</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Niche</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Country</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Onboarded</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Respondents</th>
                  <th className="text-center px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Assessments</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Signed Up</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Last Active</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {pagedData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-stone-400">
                      No free profile coaches found.
                    </td>
                  </tr>
                ) : (
                  pagedData.map((coach) => (
                    <tr key={coach.id} className="hover:bg-stone-50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-stone-900">{coach.user.fullName || '—'}</div>
                        <div className="text-sm text-stone-500 flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {coach.user.email}
                        </div>
                        <div className="text-xs text-stone-400 mt-1">Code: {coach.assessmentCode}</div>
                      </td>
                      <td className="px-4 py-3">
                        {coach.partnerName ? (
                          <span className="inline-flex items-center gap-1 text-sm text-stone-700">
                            <Building2 className="w-3.5 h-3.5 text-stone-400" /> {coach.partnerName}
                          </span>
                        ) : (
                          <span className="text-stone-400 text-sm">No Partner</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600">{coach.niche || '—'}</td>
                      <td className="px-4 py-3 text-sm text-stone-600">{coach.country || '—'}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${coach.onboardingCompleted ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`}>
                          {coach.onboardingCompleted ? 'Completed' : 'Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-stone-700">
                        <span className="inline-flex items-center gap-1">
                          <Users className="w-3.5 h-3.5 text-stone-400" /> {coach.stats.totalRespondents}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-sm text-stone-700">
                        <span className="inline-flex items-center gap-1">
                          <FileText className="w-3.5 h-3.5 text-stone-400" /> {coach.stats.completedAssessments}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600">
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5 text-stone-400" /> {formatDate(coach.createdAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600">{formatDate(coach.stats.lastActiveDate)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pageCount > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-stone-200">
              <span className="text-sm text-stone-600">
                {filteredData.length} coach{filteredData.length !== 1 ? 'es' : ''} · Page {page + 1} of {pageCount}
              </span>
              <div className="flex gap-1">
                <button onClick={() => setPage(0)} disabled={page === 0} className="p-1.5 rounded text-stone-600 hover:bg-stone-100 disabled:opacity-30">
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} className="p-1.5 rounded text-stone-600 hover:bg-stone-100 disabled:opacity-30">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button onClick={() => setPage(p => Math.min(pageCount - 1, p + 1))} disabled={page >= pageCount - 1} className="p-1.5 rounded text-stone-600 hover:bg-stone-100 disabled:opacity-30">
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button onClick={() => setPage(pageCount - 1)} disabled={page >= pageCount - 1} className="p-1.5 rounded text-stone-600 hover:bg-stone-100 disabled:opacity-30">
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
